import React, { SyntheticEvent, useEffect, useRef, useState } from 'react';
import { Box, Center, Image, Link, Radio, RadioGroup, Text, useColorModeValue, useToast } from '@chakra-ui/react';

import '../../styles/react-phone-input-2.css';

import { StatusBar } from '@capacitor/status-bar';
import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';

import packageJson from '../../../package.json';
import backgroundDark from '../../assets/images/schneider_charge_bg_dark.svg';
import backgroundLight from '../../assets/images/schneider_charge_bg_light.svg';
import schneiderLogoDark from '../../assets/images/schneider_logo_dark.svg';
import schneiderLogoLight from '../../assets/images/schneider_logo_light.svg';
import { getCurrentSession } from '../../common/api/api-helpers';
import { ArcButton } from '../../common/components/ArcButton';
import { COMPONENT_MIN_HEIGHT, IS_IOS, TOP_NAV_SPACING_AFFORDANCE } from '../../common/constants';
import {
  ENV_TYPE_SHORTHAND,
  SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY,
  SCHNEIDER_CHARGE_ENV_TYPE_USER_OVERRIDE_KEY,
} from '../../env-type';
import {
  hasDynatracePendingLogout,
  startDynatraceAuthenticationJourney,
  trackDynatraceAuthenticationFailure,
  trackDynatraceLogoutResult,
  trackDynatraceLogoutStageResult,
} from '../../utils/native/dynatrace-events';
import { openPrivacyNoticePdfInWebView } from '../account/settings/privacy/privacy-notice-pdf';
import { requestAttStatusForLogin } from './login-att';

async function completePendingIdpLogout(url: string) {
  try {
    // Keep the app on the login page while still triggering IdP session logout.
    await fetch(url, {
      method: 'GET',
      mode: 'no-cors',
      credentials: 'include',
      cache: 'no-store',
      redirect: 'follow',
    });
    return true;
  } catch (error) {
    console.warn('Background IDP logout fetch failed, falling back to hidden iframe', error);
  }

  const iframe = document.createElement('iframe');
  iframe.style.display = 'none';
  iframe.src = url;
  document.body.appendChild(iframe);

  // Best-effort cleanup for the fallback element.
  window.setTimeout(() => {
    iframe.remove();
  }, 10000);
  return false;
}

export function Login() {
  const appNameColor = useColorModeValue('primaryBranding.600', 'primaryBranding.200');
  const appVersionColor = useColorModeValue('primaryBranding.500', 'solarGreenStatic.400');
  const background = useColorModeValue(backgroundLight, backgroundDark);
  const schneiderLogo = useColorModeValue(schneiderLogoLight, schneiderLogoDark);
  const { handleMouseDown, handleMouseUp, isEnvironmentManagementVisible } = useEnvironmentSwapEasterEgg();
  const privacyLinkColor = useColorModeValue('primaryBranding.600', 'whiteAlpha.700');
  const { t, i18n } = useTranslation();
  const toast = useToast({ isClosable: true });
  const isCompletingPendingLogout = useRef(false);

  async function handleOpenPrivacyNotice() {
    try {
      await openPrivacyNoticePdfInWebView(i18n.language);
    } catch (error) {
      console.error('Failed to open Privacy Notice PDF:', error);
      toast({
        title: t('Common.something went wrong'),
        description: t('Common.please try again'),
        status: 'error',
      });
    }
  }

  useEffect(() => {
    if (IS_IOS) StatusBar.hide();

    const selectedLanguage = localStorage.getItem('i18nextLng');
    if (selectedLanguage && selectedLanguage !== i18n.language) {
      void i18n.changeLanguage(selectedLanguage);
    }

    // Step B of web logout: after Cognito redirects back to the app, complete
    // EIM session logout in the background so the main tab remains on /login
    // even if the IdP redirect target is not app-owned.
    const pendingIdpLogout = sessionStorage.getItem('pendingIdpLogout');
    if (pendingIdpLogout && !isCompletingPendingLogout.current) {
      isCompletingPendingLogout.current = true;
      sessionStorage.removeItem('pendingIdpLogout');
      trackDynatraceLogoutStageResult('cognito_logout_redirect', 'success');
      trackDynatraceLogoutStageResult('local_session_reset', 'success');
      void completePendingIdpLogout(pendingIdpLogout).then((succeeded) => {
        trackDynatraceLogoutStageResult(
          'eim_logout_request',
          succeeded ? 'success' : 'failure',
          succeeded ? undefined : 'idp_logout_request_failed'
        );
        trackDynatraceLogoutResult('success');
      });
    } else if (!isCompletingPendingLogout.current && hasDynatracePendingLogout()) {
      trackDynatraceLogoutStageResult('cognito_logout_redirect', 'success');
      trackDynatraceLogoutStageResult('local_session_reset', 'success');
      trackDynatraceLogoutStageResult('eim_logout_request', 'failure', 'idp_logout_url_missing');
      trackDynatraceLogoutResult('success');
    }
  }, [i18n]);

  return (
    // Image and logo section. The negative margin is to overlay the image over the status bar on iOS.
    <Box
      display="flex"
      px={4}
      flexDirection="column"
      h={`calc(${COMPONENT_MIN_HEIGHT} - ${TOP_NAV_SPACING_AFFORDANCE})`}
    >
      {/* Background image and branding section */}
      <Box display="flex" flexDirection="column">
        <Center pt={6}>
          <Box position="relative" w="90vw" h="90vw">
            {/* Cap the image height against the viewport so short screens (e.g. iPhone SE) keep
              the login actions and privacy link on-screen; it scales back up on taller devices. */}
            <Image src={background} alt="Schneider Charge" w="100%" h="100%" />
          </Box>
        </Center>
        {isEnvironmentManagementVisible && <EnvironmentManagement />}
        <Box px={6} pt={6}>
          <Text
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onTouchStart={handleMouseDown}
            onTouchEnd={handleMouseUp}
            fontSize={32}
            pt={2}
            color={appNameColor}
            data-testid="schneider-home"
          >
            {t('Common.schneider charge')}
          </Text>
          <Text fontSize={14} pt={2} color={appVersionColor}>
            {t('Common.version')} {packageJson.version}
          </Text>
        </Box>
      </Box>

      {/* Login actions section */}
      <Center flexDirection="column">
        <ArcButton
          my={8}
          data-testid="login-button"
          w="85%"
          arcColor="#3DCD57"
          onClick={async () => {
            startDynatraceAuthenticationJourney('login');
            await requestAttStatusForLogin();
            /* istanbul ignore next -- @preserve */
            try {
              void Auth.federatedSignIn({ customProvider: 'EIMCharge' }).catch(() => {
                trackDynatraceAuthenticationFailure('auth_session_unavailable', 'federated_sign_in');
              });
            } catch {
              trackDynatraceAuthenticationFailure('auth_session_unavailable', 'federated_sign_in');
              return;
            }
            /* istanbul ignore next -- @preserve */
            await getCurrentSession();
          }}
        >
          {t('Account.authenticate')}
        </ArcButton>

        <Image src={schneiderLogo} />

        <Link
          mt={4}
          fontSize={13}
          color={privacyLinkColor}
          textDecoration="underline"
          onClick={handleOpenPrivacyNotice}
          data-testid="login-privacy-notice-link"
        >
          {t('Privacy Notice.privacy notice')}
        </Link>
      </Center>
    </Box>
  );
}

const EASTER_EGG_WAIT_TIME_MS = Number(import.meta.env.VITE_EASTER_EGG_WAIT_TIME_MS) || 45000;

function useEnvironmentSwapEasterEgg() {
  const isMouseDown = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [isEnvironmentManagementVisible, setIsEnvironmentManagementVisible] = useState(false);

  function handleMouseDown(e: SyntheticEvent) {
    e.preventDefault();
    isMouseDown.current = true;

    // Guard against stacking timers if a previous press never resolved (e.g. rapid re-press).
    clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (isMouseDown.current) {
        setIsEnvironmentManagementVisible(true);
      }
    }, EASTER_EGG_WAIT_TIME_MS);
  }

  function handleMouseUp() {
    isMouseDown.current = false;
    clearTimeout(timeoutRef.current);
  }

  return {
    handleMouseDown,
    handleMouseUp,
    isEnvironmentManagementVisible,
  };
}

function EnvironmentManagement() {
  return (
    <>
      <Box mb={1} data-testid="environment-select">
        Select environment
      </Box>
      <RadioGroup
        onChange={(e) => {
          localStorage.setItem(SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY, e.toString());
          localStorage.setItem(SCHNEIDER_CHARGE_ENV_TYPE_USER_OVERRIDE_KEY, 'true');
          window.location.reload();
        }}
        mb={4}
        value={ENV_TYPE_SHORTHAND}
      >
        <Radio mr={2} value="PROD">
          Production
        </Radio>
        <Radio ml={2} value="STAGING">
          Staging
        </Radio>
      </RadioGroup>
    </>
  );
}
